import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Guide data from the backend.
 */
interface Guide {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    icon: string;
    order: number;
    is_published: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Props for the University Index page.
 */
interface Props {
    guides: Guide[];
}

/**
 * GM University Index - displays all guides with management actions.
 */
export default function Index({ guides }: Props) {
    const handleDelete = (guide: Guide) => {
        if (confirm(`Are you sure you want to delete "${guide.title}"?`)) {
            router.delete(`/gm/university/${guide.id}`, { preserveScroll: true });
        }
    };

    return (
        <GmLayout title="University Guides" activePath="/gm/university">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">🎓 University Guides</h1>
                    <Link
                        href="/gm/university/create"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                        + Create Guide
                    </Link>
                </div>

                <p className="text-gray-400 mb-6">
                    Manage game guides and tutorials. These guides help players learn how to play Sellune.
                </p>

                {/* Guides Table */}
                {guides.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 bg-slate-800 border border-slate-700 rounded-lg">
                        No guides created yet. Create your first guide to help players learn the game.
                    </div>
                ) : (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Order</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Guide</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Updated</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {guides.map((guide) => (
                                        <tr key={guide.id}>
                                            {/* Order */}
                                            <td className="px-4 py-3 text-sm text-gray-400">
                                                {guide.order}
                                            </td>

                                            {/* Guide */}
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-2xl">{guide.icon}</span>
                                                    <div>
                                                        <Link
                                                            href={`/gm/university/${guide.id}/edit`}
                                                            className="text-white font-medium hover:text-blue-400"
                                                        >
                                                            {guide.title}
                                                        </Link>
                                                        <p className="text-xs text-gray-500">/university/{guide.slug}</p>
                                                        {guide.description && (
                                                            <p className="text-xs text-gray-400 mt-1 max-w-md truncate">
                                                                {guide.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-1 text-xs rounded ${guide.is_published ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'}`}>
                                                    {guide.is_published ? 'Published' : 'Draft'}
                                                </span>
                                            </td>

                                            {/* Updated */}
                                            <td className="px-4 py-3 text-sm text-gray-400">
                                                {guide.updated_at ? new Date(guide.updated_at).toLocaleDateString() : 'N/A'}
                                            </td>

                                            {/* Actions */}
                                            <td className="px-4 py-3">
                                                <div className="flex gap-2">
                                                    <Link
                                                        href={`/gm/university/${guide.id}/edit`}
                                                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                                                    >
                                                        Edit
                                                    </Link>
                                                    <a
                                                        href={`/university/${guide.slug}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="px-3 py-1 text-xs bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                                                    >
                                                        View
                                                    </a>
                                                    <button
                                                        onClick={() => handleDelete(guide)}
                                                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </GmLayout>
    );
}
